import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Moon, Sun, Menu, X, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import TransitionLink from './TransitionLink';
import TransitionNavLink from './TransitionNavLink';

interface NavbarProps {
  darkMode: boolean;
  setDarkMode: (value: boolean) => void;
  menuOpen: boolean;
  setMenuOpen: (value: boolean) => void;
}

/**
 * Optimus-style navigation: transparent full-width bar that condenses into a
 * floating blurred pill on scroll, with a full-screen mobile menu.
 */
const Navbar: React.FC<NavbarProps> = ({ darkMode, setDarkMode, menuOpen, setMenuOpen }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const navItems = [
    { to: '/', label: t('nav.home') },
    { to: '/projects', label: t('nav.portfolio') },
    { to: '/blog', label: t('nav.blog') },
    { to: '/about', label: t('nav.about') },
    { to: '/chat', label: t('nav.chat') },
    { to: '/contact', label: t('nav.contact') },
  ];

  useEffect(() => {
    // The site's base CSS gives <body> height:100% + overflow auto, which makes
    // body (not window) the scroll container — listen on both to be safe.
    const getScrollY = () =>
      window.scrollY || document.body.scrollTop || document.documentElement.scrollTop || 0;
    const handleScroll = () => {
      setIsScrolled(getScrollY() > 20);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.body.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.body.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language.startsWith('ar') ? 'en' : 'ar');
  };
  const isArabic = i18n.language.startsWith('ar');

  // The hero now follows the theme (dark ink on cream in light mode, light ink
  // on near-black in dark mode), so foreground tokens read correctly in every
  // state — no special-casing for a transparent-over-dark bar is needed.
  const inkSubtle = 'text-foreground/70 hover:text-foreground';
  const inkStrong = 'text-foreground';
  const underline = 'bg-foreground';

  return (
    <header
      className={`op-nav fixed z-[9999] font-instrument transition-all duration-500 ${
        isScrolled ? 'top-4 left-4 right-4' : 'top-0 left-0 right-0'
      }`}
    >
      {/* Mobile Menu - Full Screen Overlay (below the bar so its buttons stay clickable) */}
      <div
        className={`lg:hidden fixed inset-0 z-40 bg-background text-foreground transition-all duration-500 ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="flex flex-col h-full px-8 pt-28 pb-8">
          {/* Navigation Links */}
          <div className="flex-1 flex flex-col justify-center gap-7">
            {navItems.map((item, i) => (
              <TransitionNavLink
                key={item.to}
                to={item.to}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }: { isActive: boolean }) =>
                  `text-5xl font-display transition-all duration-500 ${
                    isActive ? 'text-muted-foreground' : 'text-foreground hover:text-muted-foreground'
                  } ${menuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`
                }
              >
                <span style={{ transitionDelay: menuOpen ? `${i * 75}ms` : '0ms' }} className="block">
                  {item.label}
                </span>
              </TransitionNavLink>
            ))}
          </div>

          {/* Bottom controls */}
          <div
            className={`flex items-center gap-4 pt-8 border-t border-foreground/10 transition-all duration-500 ${
              menuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: menuOpen ? '300ms' : '0ms' }}
          >
            <button
              type="button"
              onClick={() => setDarkMode(!darkMode)}
              className="w-14 h-14 rounded-full border border-foreground/20 flex items-center justify-center hover:bg-foreground/5 transition-colors"
              aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button
              type="button"
              onClick={toggleLanguage}
              className="h-14 px-5 rounded-full border border-foreground/20 flex items-center gap-2 hover:bg-foreground/5 transition-colors"
              aria-label={t('common.language')}
            >
              <Globe size={18} />
              <span className="text-sm font-mono">{isArabic ? 'EN' : 'عربي'}</span>
            </button>
            <TransitionLink
              to="/contact"
              onClick={() => setMenuOpen(false)}
              className="flex-1 h-14 rounded-full bg-foreground text-background flex items-center justify-center text-base font-medium"
            >
              {t('op.nav.cta')}
            </TransitionLink>
          </div>
        </div>
      </div>

      <nav
        className={`relative z-50 mx-auto transition-all duration-500 ${
          isScrolled || menuOpen
            ? 'bg-background/80 backdrop-blur-xl border border-foreground/10 rounded-2xl shadow-lg max-w-[1200px] text-foreground'
            : 'bg-transparent max-w-[1400px]'
        }`}
      >
        <div
          className={`flex items-center justify-between transition-all duration-500 px-6 lg:px-8 ${
            isScrolled ? 'h-14' : 'h-20'
          }`}
        >
          {/* Logo */}
          <TransitionLink to="/" className={`flex items-center gap-2 group ${inkStrong}`}>
            <span
              className={`font-display tracking-tight transition-all duration-500 ${
                isScrolled ? 'text-xl' : 'text-2xl'
              }`}
            >
              Talal Alkhaled
            </span>
          </TransitionLink>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-10">
            {navItems.map((item) => (
              <TransitionNavLink
                key={item.to}
                to={item.to}
                className={({ isActive }: { isActive: boolean }) =>
                  `text-sm transition-colors duration-300 relative group ${inkSubtle} ${
                    isActive ? '!text-foreground' : ''
                  }`
                }
              >
                {item.label}
                <span
                  className={`absolute -bottom-1 start-0 w-0 h-px transition-all duration-300 group-hover:w-full ${underline} ${
                    location.pathname === item.to ? 'w-full' : ''
                  }`}
                />
              </TransitionNavLink>
            ))}
          </div>

          {/* Desktop controls */}
          <div className="hidden lg:flex items-center gap-5">
            <button
              type="button"
              onClick={toggleLanguage}
              className={`flex items-center gap-1.5 transition-all duration-500 ${inkSubtle} ${
                isScrolled ? 'text-xs' : 'text-sm'
              }`}
              aria-label={t('common.language')}
            >
              <Globe size={isScrolled ? 14 : 16} />
              <span className="font-mono">{isArabic ? 'EN' : 'عربي'}</span>
            </button>

            <button
              type="button"
              onClick={() => setDarkMode(!darkMode)}
              className={`transition-colors duration-300 ${inkSubtle}`}
              aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? <Sun size={isScrolled ? 16 : 18} /> : <Moon size={isScrolled ? 16 : 18} />}
            </button>

            <TransitionLink
              to="/contact"
              className={`inline-flex items-center justify-center rounded-full font-medium bg-foreground text-background hover:bg-foreground/90 transition-all duration-500 ${
                isScrolled ? 'px-4 h-8 text-xs' : 'px-6 h-10 text-sm'
              }`}
            >
              {t('op.nav.cta')}
            </TransitionLink>
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            className={`lg:hidden p-2 ${menuOpen ? 'text-foreground' : inkStrong}`}
            aria-label={t('common.menu')}
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
